<template>
  <div class="spinner-content">
    <transition
      name="fade"
      mode="out-in"
    >
      <div
        v-if="isTransferringRunning"
        key="loading"
      >
        <div class="loader-progress large" />
        <div class="loading-text">
          {{ progress }} de {{ total }}
        </div>
        <p>{{ message }}</p>
      </div>
      <div
        v-else
        key="sucess"
      >
        <component
          :is="reportType"
          :transfer-reports="transferReports"
          :push-to-print="pushToPrint"
          :push-to-orders-list="pushToOrdersList"
          :push-to-failed-orders="pushToFailedOrders"
          :patient-link="patientLink"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import OrderServices from '@/api/order'
import { mapGetters, mapActions } from 'vuex'
import FailedTransfer from './templates/Failed'
import HalfTransfer from './templates/Half'
import SuccessTransfer from './templates/Success'
import TimeOutTransfer from './templates/Timeout'
import LoadingTransfer from './templates/Loading'
import RetryReport from './templates/Retry'
import consentService from '@/api/consent'
import {
  SPINNER_TRANSFER_COUNT_MILISECONDS,
  SPINNER_TRANSFER_TIMEOUT_MAX_SECONDS,
  SPINNER_TRANSFER_DEFAULT_MESSAGE,
  SPINNER_TRANSFER_FIRST_TIMEOUT_SECONDS,
  SPINNER_TRANSFER_FIRST_TIMEOUT_MESSAGE,
  SPINNER_TRANSFER_SECOND_TIMEOUT_SECONDS,
  SPINNER_TRANSFER_SECOND_TIMEOUT_MESSAGE,
  ORDER_STATE_FAIL
} from '@/enums'
import Moment from 'moment'

const STATUS_SUCCESS = 'Concluído'
const STATUS_LOADING = 'Aguarde'
const STATUS_FAILED = 'Parece que algo deu errado…'

export default {
  name: 'SpinnerProgress',
  components: {
    'failed-transfer': FailedTransfer,
    'half-transfer': HalfTransfer,
    'success-transfer': SuccessTransfer,
    'timeout-transfer': TimeOutTransfer,
    'loading-transfer': LoadingTransfer,
    'retry-report': RetryReport
  },
  props: [
    'patientLink',
    'orderSelected',
    'isModalOpen',
    'transferringOS',
    'closedTransferModal',
    'examType'
  ],
  data: () => ({
    pending: null,
    interval: null,
    isTransferringRunning: true,
    transferred: null,
    countingFailed: false,
    lastProgress: 0,
    currentTimeoutCount: 0,
    analyticsTime: {},
    message: SPINNER_TRANSFER_DEFAULT_MESSAGE,
    modalTitle: STATUS_LOADING
  }),
  computed: {
    ...mapGetters(['preference', 'profile', 'transferReports', 'transferReportLoadingStatus', 'isConsentForm', 'isExamNumber', 'toggle']),
    progress: function () {
      return this.transferred < 0 || !this.transferred ? 0 : this.transferred
    },
    total: function () {
      return this.orderSelected.length
    },
    isCounterCompleteOrFailed: function () {
      return this.progress === this.total || this.countingFailed
    },
    isCounterStucked: function () {
      return this.transferred === this.lastProgress
    },
    reportType: function () {
      if (this.transferReportLoadingStatus === 'STATUS_LOADING') {
        this.modalTitle = STATUS_LOADING
        return 'loading-transfer'
      } else if (this.transferReportLoadingStatus === 'STATUS_FAILED') {
        this.modalTitle = STATUS_SUCCESS
        return 'retry-report'
      } else {
        this.modalTitle = STATUS_SUCCESS
        if (this.toggle.consentForm && this.examType === 'SEXA') this.setConsentForm()
        if (
          this.transferReports.failTransmitOrdersCount > 0 &&
          this.transferReports.samplesCount === 0 &&
          !this.countingFailed
        ) {
          return 'failed-transfer'
        } else if (
          this.transferReports.samplesCount > 0 &&
          this.transferReports.failTransmitOrdersCount > 0 &&
          !this.countingFailed
        ) {
          return 'half-transfer'
        } else if (
          this.transferReports.samplesCount > 0 &&
          this.transferReports.failTransmitOrdersCount === 0 &&
          !this.countingFailed
        ) {
          return 'success-transfer'
        } else if (this.countingFailed) {
          this.clearLastTransferredList()
          this.modalTitle = STATUS_FAILED
          return 'timeout-transfer'
        }
      }
    }
  },
  watch: {
    pending: function () {
      this.saveTimeOfLastProgress()
    },
    isTransferringRunning: function (newValue) {
      this.$emit('update:isLoading', newValue)
    },
    modalTitle: function (newValue) {
      this.$emit('update:modalTitle', newValue)
    }
  },
  created() {
    this.saveTimeOfLastProgress()
    this.runCounter()
  },
  beforeDestroy() {
    clearInterval(this.interval)
    this.clearData()
    this.resetData()
  },
  methods: {
    ...mapActions(['getTotalSamplesFromTransferring', 'clearLastTransferredList']),
    runCounter() {
      this.analyticsTime.startTime = Moment(new Date())

      this.interval = setInterval(
        function () {
          if (this.isModalOpen) {
            if (!this.isCounterCompleteOrFailed) {
              this.keepCounting()
            } else {
              this.hideCounterAndShowReport()
            }
          }
        }.bind(this),
        SPINNER_TRANSFER_COUNT_MILISECONDS
      )
    },
    updatePending() {
      var _self = this
      OrderServices.countOsTransmitting().then(function (response) {
        _self.pending = response.data.ordersTransmittingCount
      })
    },
    calculateProgress() {
      this.transferred = parseInt(this.total) - parseInt(this.pending)
    },
    keepCounting() {
      this.checkCountConsistency()
      this.updatePending()
      this.calculateProgress()
    },
    saveTimeOfLastProgress() {
      this.lastProgress = this.transferred
    },
    countTimeout() {
      if (this.isCounterStucked) {
        this.currentTimeoutCount++
      } else {
        this.currentTimeoutCount = 0
        this.lastProgress = this.transferred
      }
    },
    checkTimeout() {
      this.countTimeout()
      if (this.currentTimeoutCount < SPINNER_TRANSFER_FIRST_TIMEOUT_SECONDS) {
        this.message = SPINNER_TRANSFER_DEFAULT_MESSAGE
      } else if (
        this.currentTimeoutCount >= SPINNER_TRANSFER_FIRST_TIMEOUT_SECONDS &&
        this.currentTimeoutCount < SPINNER_TRANSFER_SECOND_TIMEOUT_SECONDS
      ) {
        this.message = SPINNER_TRANSFER_FIRST_TIMEOUT_MESSAGE
      } else if (
        this.currentTimeoutCount >= SPINNER_TRANSFER_SECOND_TIMEOUT_SECONDS &&
        this.currentTimeoutCount < SPINNER_TRANSFER_TIMEOUT_MAX_SECONDS
      ) {
        this.message = SPINNER_TRANSFER_SECOND_TIMEOUT_MESSAGE
      } else if (this.currentTimeoutCount >= SPINNER_TRANSFER_TIMEOUT_MAX_SECONDS) {
        this.breakCounting()
      }
    },
    resetData() {
      this.pending = null
      this.transferred = null
      this.lastProgress = 0
      this.currentTimeoutCount = 0
    },
    checkMutalTransferring() {
      if (this.transferred < 0) {
        this.breakCounting()
      }
    },
    hideCounterAndShowReport() {
      this.getTotalSamplesFromTransferring()
      this.isTransferringRunning = false

      clearInterval(this.interval)

      this.pushTransferTimeToAnalytics()
      this.pushEventToAnalytics(this.reportType)
      this.resetData()
    },
    breakCounting() {
      this.countingFailed = true
      this.isTransferringRunning = false
      clearInterval(this.interval)

      this.pushTransferTimeToAnalytics()
    },
    checkCountConsistency() {
      this.checkMutalTransferring()
      this.checkTimeout()
    },
    pushToOrdersList() {
      this.clearLastTransferredList()
      this.closedTransferModal()
    },
    pushToFailedOrders() {
      this.closedTransferModal()
      this.$router.push({
        path: 'orders',
        query: {
          filterBy: ORDER_STATE_FAIL,
          onlyFailedFromLastTransfer: true
        }
      })
    },
    pushToPrint() {
      this.clearLastTransferredList()
      this.$router.push('print-tag/batches')
    },
    clearData() {
      this.countingFailed = false
      this.isTransferringRunning = true
      this.interval = null
      this.currentTimeoutCount = 0
    },
    pushEventToAnalytics(action) {
      let profile = this.profile
      this.$analytics.pushAnalyticsEvent('ga-event', {
        category: 'Spinner - Transferencia',
        action: action,
        'id-user': profile.id,
        'id-lab': profile.id_laboratory,
        'name-lab': profile.laboratoryName
      })
    },
    pushTransferTimeToAnalytics() {
      this.analyticsTime.endTime = Moment(new Date())

      let totalTimeSpentToTransfer = this.analyticsTime.endTime.diff(this.analyticsTime.startTime, 'seconds')

      let profile = this.profile
      this.$analytics.pushAnalyticsEvent('ga-event', {
        category: 'Spinner - Transferencia',
        action: 'Tempo de Transferencia',
        label: totalTimeSpentToTransfer,
        value: totalTimeSpentToTransfer,
        'id-user': profile.id,
        'id-lab': profile.id_laboratory,
        'name-lab': profile.laboratoryName,
        'total-os': this.total
      })
    },
    async setConsentForm() {
      try {
        await consentService.updated(this.isConsentForm, this.isExamNumber)
      } catch (error) {
        console.log(error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.spinner-content {
  .transfer {
    width: 340px;
    margin-top: 5px;
    margin-bottom: 5px;
    border-radius: 2px;
    letter-spacing: -0.1px;
    text-align: center;
  }

  .messageTransfer {
    display: flex;
    flex-direction: row;
  }

  .iconTransfer {
    flex: 1;
    padding-bottom: 10px;
  }

  div.md-dialog-content {
    text-align: start;
    flex-direction: row;
    margin-right: 10px;
    margin-left: 10px;
  }

  .failedAllOrders {
    font-family: Roboto, sans-serif;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    font-size: 37px;
    color: #c20c00;
  }

  p {
    cursor: default;

    .contentModal {
      font-family: Roboto, sans-serif;
      font-style: normal;
      font-weight: normal;
      font-weight: 900;
      color: #617173;
      line-height: 19px;
      font-size: 18px;
    }
  }

  .md-dialog-title {
    font-family: Roboto, sans-serif;
    font-style: normal;
    font-weight: 500;
    color: #4a4a4a;
    font-size: 25px;
  }

  .loader-progress,
  .loader-progress:after {
    border-radius: 50%;
    width: 10em;
    height: 10em;
  }

  .loader-progress {
    margin: 0 auto;
    content: '';
    margin-bottom: 20px;
    font-size: 10px;
    position: relative;
    border-top: 1.1em solid rgba(127, 26, 84, 0.2);
    border-right: 1.1em solid rgba(127, 26, 84, 0.2);
    border-bottom: 1.1em solid rgba(127, 26, 84, 0.2);
    border-left: 1.1em solid #7f1a54;
    -webkit-transform: translateZ(0);
    -ms-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-animation: load8 0.4s infinite linear;
    animation: load8 0.4s infinite linear;

    &.small {
      border-width: 3px;
      width: 2em;
      height: 2em;
    }

    &.medium {
      border-width: 4px;
      width: 4em;
      height: 4em;
    }

    &.large {
      border-width: 15px;
      width: 13em;
      height: 13em;
    }
  }

  .loading-text {
    width: 90px;
    position: absolute;
    top: 58px;
    left: calc(50% - 45px);
    text-align: center;
    font-size: 16px;
    -webkit-animation: none !important;
    animation: none !important;
    cursor: progress;
    color: #7f1a54;
    font-weight: bold;
  }

  .loading-sucess {
    font-size: 40px;
    align-content: center;
    align-items: center;
  }

  .material-icons-sucess {
    font-family: 'Material Icons';
    font-weight: normal;
    font-style: normal;
    font-size: 155px;
    text-transform: none;
    display: inline-block;
    white-space: nowrap;
    word-wrap: normal;
    direction: ltr;
    -moz-font-feature-settings: 'liga';
    -moz-osx-font-smoothing: grayscale;
    cursor: default;
    height: 130px;
    width: 130px;
  }

  @-webkit-keyframes load8 {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }

    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }

  @keyframes load8 {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }

    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }

  @-webkit-keyframes load9 {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }

    100% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
  }

  @keyframes load9 {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }

    100% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.5s;
  }

  .fade-enter,
  .fade-leave-to

  /* .fade-leave-active em versões anteriores a 2.1.8 */
    {
    opacity: 0;
  }

  .spinner-success-text {
    padding-bottom: 25px !important;
    padding-top: 30px !important;
  }

  .spinner-succes-button {
    margin-right: 15px;
  }

  p {
    text-align: start;
    font-size: 15px;
    padding: 20px 0 60px;
  }

  .deletetion-text {
    text-align: center;
    font-size: 18px;
    padding: 0px;
  }

  .md-dialog {
    max-width: 35%;
    padding-right: 10px;
    padding-left: 10px;
  }

  p {
    font-family: Roboto, sans-serif;
    font-style: normal;
    font-weight: 400;
    color: #617173;
    line-height: 19px;
    font-size: 16px;
    justify-content: center;
    padding: 30px 0 20px 0;
    text-align: center;
  }
}

.transfer-modal {
  &.modals .md-dialog {
    width: 360px;
    max-width: 360px;
    min-height: 27em;
  }

  .main-title {
    font-family: Roboto, sans-serif;
    font-style: normal;
    font-weight: 500;
    font-size: 18px;
    line-height: normal;
    color: #4a4a4a;
  }

  .md-gutter-40:not(.md-column)>.md-layout {
    padding-right: 15px;
    padding-left: 15px;
  }
}
</style>
